import React from "react";
import Grid from "@mui/material/Grid";
import PatientProfile from "../../components/PatientProfile";
import FactsPanel from "./FactsPanel";
import { buildVitalsData, SingleVitalChart } from "../../components/Chat/Chart/CompactVitalsChart";
import Chat from "../../components/Chat";
import { ShareConversationButton } from "../../components/ShareConversation";
import { getSharedWithMe } from "../../components/ShareConversation/requests";
import {
  Box,
  Tab,
  Tabs,
  Skeleton,
  useTheme,
} from "@mui/material";
import AllRuns, { DEFAULT_COPILOT_MODEL } from "./AllRuns";
import LastReadingCard from "./LastReadingCard";
import { v4 as uuid } from "uuid";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import Sidebar from "./Sidebar";
import * as S from "./style";
import { ramps, rolesFor, vitalsColors, fonts } from "../../common/theme/brandTokens";
import { useCallback } from "react";
import { useAuth } from "../../contexts/AuthContext";
import {
  createUserConversation,
  getCopilotResponsesByRunId,
  getConversationByShortCode,
  getUserConversations,
  updateConversationStatus,
  updateConversationSummary,
  deleteConversation,
  getPatientMetadata,
  getModelRunsByPatientId,
  getPatientTimeSeries,
  getPatientForecasts,
  getPatientThresholds,
} from "./requests";
import { ROUTES } from "common/constant";
import { useLanguage } from "../../contexts/LanguageContext";
import { useStreamingIds } from "../../contexts/ChatStreamContext";

export default function Patient() {
  const { user } = useAuth();
  const { language, t } = useLanguage();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const { shortcode, patientId: patientIdParam } = useParams<{ shortcode?: string; patientId?: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [patientId, setPatientId] = React.useState<string | null>(null);
  const [modelRunId, setModelRunId] = React.useState<string | null>(null);
  const [selectedModel, setSelectedModel] = React.useState<string>(DEFAULT_COPILOT_MODEL);
  const [meta, setMeta] = React.useState<any>(null);
  const [modelRuns, setModelRuns] = React.useState<any[]>([]);
  const [totalRunCount, setTotalRunCount] = React.useState(0);
  const modelRunsRef = React.useRef<any[]>([]);
  // Keep ref in sync so callbacks can look up runs without stale closures
  React.useEffect(() => { modelRunsRef.current = modelRuns; }, [modelRuns]);
  const patientIdRef = React.useRef<string | null>(null);
  React.useEffect(() => { patientIdRef.current = patientId; }, [patientId]);
  const dateOptions = React.useMemo(() => {
    return modelRuns
      .filter((run) => run?.end_date)
      .map((run) => ({ runId: run.model_run_id as string, date: new Date(run.end_date) }))
      .slice(0, 5)
      .reverse();
  }, [modelRuns]);

  const [selectedDate, setSelectedDate] = React.useState<string | null>(null);
  const [isHistoryOpen, setIsHistoryOpen] = React.useState(true);
  const [conversationId, setConversationId] = React.useState<string | null>(null);
  // Owner of the currently-viewed conversation, populated when a conversation is
  // loaded by short code. Null for new/temp conversations (the current user owns
  // those). Drives the shared-viewer / "Shared by" badge logic below.
  const [conversationOwner, setConversationOwner] = React.useState<
    { id: string; name: string | null; email: string | null; avatarUrl: string | null } | null
  >(null);
  const [pendingConversationId, setPendingConversationId] = React.useState<string | null>(null);
  const [copilotResponseText, setCopilotResponseText] = React.useState<string | null>(null);
  const [copilotResponseMeta, setCopilotResponseMeta] = React.useState<{ run_id: string; created_at: string; tft_model?: string | null; copilot_model?: string | null } | null>(null);
  const [isTranslating, setIsTranslating] = React.useState(false);
  const [conversations, setConversations] = React.useState<
    { uuid: string; text: string; lastUpdated: string; shortCode: string; status?: string; modelRunId?: string; shared?: boolean }[]
  >([]);
  // IDs of every conversation shared with the current user (across patients),
  // used to detect when the open conversation is one shared with (not owned by)
  // them — robust regardless of the owner-record load timing.
  const [sharedConvoIds, setSharedConvoIds] = React.useState<Set<string>>(new Set());
  // Conversations currently generating (background streams included), owned by
  // the app-level ChatStreamProvider so the indicator survives navigation.
  const streamingIds = useStreamingIds();
  const conversationRunIdRef = React.useRef<string | null>(null);
  const [runVitals, setRunVitals] = React.useState<{
    charttime: string[];
    heart_rate: (number | null)[];
    systolic_blood_pressure: (number | null)[];
    diastolic_blood_pressure: (number | null)[];
    o2_saturation: (number | null)[];
  } | null>(null);

  type ForecastPoint = { time_idx: number; value: number; pred_interval_lo: number; pred_interval_hi: number };
  type RunForecasts = {
    systolic_blood_pressure: ForecastPoint[];
    diastolic_blood_pressure: ForecastPoint[];
    heart_rate: ForecastPoint[];
    o2_saturation: ForecastPoint[];
  };
  const [runForecasts, setRunForecasts] = React.useState<RunForecasts | null>(null);
  const [thresholds, setThresholds] = React.useState<any>(null);
  const [isLoadingVitals, setIsLoadingVitals] = React.useState(false);
  const [isLoadingThresholds, setIsLoadingThresholds] = React.useState(false);
  const loadingVitalsTimerRef = React.useRef<number | null>(null);
  // Monotonic token used to cancel stale copilot polls when the patient, run,
  // or language changes while a previous poll is still in flight.
  const copilotPollTokenRef = React.useRef(0);

  const [sideTab, setSideTab] = React.useState<0 | 1>(0);

  const formatRunLabel = (date: Date, showTime: boolean) => {
    const localeMap: Record<string, string> = { en: 'en-US', no: 'nb-NO' };
    const locale = localeMap[language] || 'en-US';
    const dateStr = new Intl.DateTimeFormat(locale, { month: "short", day: "numeric" }).format(date);
    if (!showTime) return dateStr;
    const timeStr = new Intl.DateTimeFormat(locale, { hour: "2-digit", minute: "2-digit" }).format(date);
    return `${dateStr} ${timeStr}`;
  };

  const parseForecastRows = (rows: any[]): RunForecasts => {
    const out: RunForecasts = {
      systolic_blood_pressure: [],
      diastolic_blood_pressure: [],
      heart_rate: [],
      o2_saturation: [],
    };
    for (const row of rows) {
      const key = row.vital_type as keyof RunForecasts;
      if (key in out) {
        out[key].push({
          time_idx: row.time_idx,
          value: row.value,
          pred_interval_lo: row.pred_interval_lo,
          pred_interval_hi: row.pred_interval_hi,
        });
      }
    }
    for (const k of Object.keys(out) as (keyof RunForecasts)[]) {
      out[k].sort((a, b) => a.time_idx - b.time_idx);
    }
    return out;
  };

  const parseTimeSeriesRows = (rows: any[]) => {
    const byIdx: Record<number, Record<string, any>> = {};
    for (const row of rows) {
      if (!byIdx[row.time_idx]) byIdx[row.time_idx] = { charttime: row.charttime };
      byIdx[row.time_idx][row.vital_type] = row.value;
    }
    const sortedIdxs = Object.keys(byIdx).map(Number).sort((a, b) => a - b);
    const charttime: string[] = [];
    const heart_rate: (number | null)[] = [];
    const systolic_blood_pressure: (number | null)[] = [];
    const diastolic_blood_pressure: (number | null)[] = [];
    const o2_saturation: (number | null)[] = [];
    for (const idx of sortedIdxs) {
      const entry = byIdx[idx];
      charttime.push(entry.charttime);
      heart_rate.push(entry.heart_rate ?? null);
      systolic_blood_pressure.push(entry.systolic_blood_pressure ?? null);
      diastolic_blood_pressure.push(entry.diastolic_blood_pressure ?? null);
      o2_saturation.push(entry.o2_saturation ?? null);
    }
    return { charttime, heart_rate, systolic_blood_pressure, diastolic_blood_pressure, o2_saturation };
  };

  // Fetch the cached copilot response, polling while the backend generates it.
  // The GET endpoint returns 200 with a row once cached, or 202 while generation
  // is still in progress. Resolves `{ superseded }` = true when a newer fetch
  // (patient/run/language switch) has taken over — callers must then leave shared
  // state (including `isTranslating`) alone, since the newer fetch now owns it.
  const pollCopilotResponse = useCallback(
    async (runId: string, lang: string): Promise<{ superseded: boolean; record: any | null }> => {
      const token = ++copilotPollTokenRef.current;
      const POLL_INTERVAL_MS = 4000;
      const MAX_ATTEMPTS = 30; // ~2 minutes
      for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
        if (token !== copilotPollTokenRef.current) return { superseded: true, record: null };
        try {
          const resp = await getCopilotResponsesByRunId(runId, lang);
          if (token !== copilotPollTokenRef.current) return { superseded: true, record: null };
          if (resp?.status === 200) {
            return { superseded: false, record: resp?.data?.[0] || null };
          }
          // 202 (or any other non-200 success): still generating — keep polling.
        } catch {
          return { superseded: false, record: null };
        }
        await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
      }
      return { superseded: false, record: null }; // gave up after MAX_ATTEMPTS
    },
    [],
  );

  const switchToRun = useCallback((runId: string, lang?: string) => {
    setSelectedDate(runId);
    setModelRunId(runId);
    setIsTranslating(true);
    const targetLang = lang ?? language;
    pollCopilotResponse(runId, targetLang)
      .then(({ superseded, record }) => {
        if (superseded) return; // a newer fetch owns the loading state now
        setCopilotResponseText(record?.text || null);
        if (record?.run_id && record?.created_at) {
          const run = modelRunsRef.current.find((r) => r.model_run_id === runId);
          setCopilotResponseMeta({
            run_id: record.run_id,
            created_at: record.created_at,
            tft_model: run?.tft_model ?? null,
            copilot_model: run?.copilot_model ?? null,
          });
        } else {
          setCopilotResponseMeta(null);
        }
        setIsTranslating(false);
      });

    if (loadingVitalsTimerRef.current) clearTimeout(loadingVitalsTimerRef.current);
    loadingVitalsTimerRef.current = window.setTimeout(() => setIsLoadingVitals(true), 250);
    getPatientTimeSeries(runId)
      .then((tsResp) => {
        const rows = Array.isArray(tsResp?.data) ? tsResp.data : [];
        setRunVitals(rows.length ? parseTimeSeriesRows(rows) : null);
      })
      .catch(() => setRunVitals(null))
      .finally(() => {
        if (loadingVitalsTimerRef.current) { clearTimeout(loadingVitalsTimerRef.current); loadingVitalsTimerRef.current = null; }
        setIsLoadingVitals(false);
      });
    getPatientForecasts(runId)
      .then((fcResp) => {
        const rows = Array.isArray(fcResp?.data) ? fcResp.data : [];
        setRunForecasts(rows.length ? parseForecastRows(rows) : null);
      })
      .catch(() => setRunForecasts(null));
  }, [language, pollCopilotResponse]);

  React.useEffect(() => {
    return () => {
      if (loadingVitalsTimerRef.current) {
        clearTimeout(loadingVitalsTimerRef.current);
        loadingVitalsTimerRef.current = null;
      }
    };
  }, []);

  React.useEffect(() => {
    if (!patientId) return;
    setCopilotResponseText(null);
    setCopilotResponseMeta(null);
    setModelRunId(null);
    setRunVitals(null);
    setRunForecasts(null);
    setSelectedDate(null);
    setIsTranslating(true);

    // Fetch patient thresholds
    setIsLoadingThresholds(true);
    getPatientThresholds(patientId)
      .then((res) => {
        setThresholds(res?.data || null);
        setIsLoadingThresholds(false);
      })
      .catch(() => {
        setThresholds(null);
        setIsLoadingThresholds(false);
      });
    getModelRunsByPatientId(patientId, 50, 0)
      .then((res) => {
        const runs = Array.isArray(res.data) ? res.data : [];
        setTotalRunCount(res.totalCount);
        setModelRuns(runs);
        const linkedRunId = conversationRunIdRef.current;
        conversationRunIdRef.current = null;
        const searchRunId = searchParams.get("run");
        // The conversation's run (or an explicit ?run= param) may be older than
        // the newest 50 runs loaded here, so `runs.find` can miss it. All
        // downstream fetches (vitals, forecasts, copilot) go by run id, so
        // select the linked run directly even when it's not in the loaded page
        // instead of silently falling back to the latest run (e.g. on refresh
        // of a /c/:shortcode URL for an older conversation).
        const linkedRun = linkedRunId
          ? runs.find((r) => r.model_run_id === linkedRunId) ?? { model_run_id: linkedRunId }
          : null;
        const searchRun = searchRunId
          ? runs.find((r) => r.model_run_id === searchRunId) ?? { model_run_id: searchRunId }
          : null;
        const preferred = searchRun ?? linkedRun ?? runs[0];
        if (!preferred?.model_run_id) {
          setIsTranslating(false);
          return;
        }
        const runId = preferred.model_run_id as string;
        setModelRunId(runId);
        setSelectedDate(runId);
        if (patientIdParam && !searchParams.get("run")) {
          navigate({ pathname: `${ROUTES.PATIENT_BY_ID}/${patientIdParam}`, search: `?run=${runId}` }, { replace: true });
        }
        if (loadingVitalsTimerRef.current) clearTimeout(loadingVitalsTimerRef.current);
        loadingVitalsTimerRef.current = window.setTimeout(() => setIsLoadingVitals(true), 250);
        getPatientTimeSeries(runId)
          .then((tsResp) => {
            const rows = Array.isArray(tsResp?.data) ? tsResp.data : [];
            if (rows.length) setRunVitals(parseTimeSeriesRows(rows));
          })
          .catch(() => setRunVitals(null))
          .finally(() => {
            if (loadingVitalsTimerRef.current) { clearTimeout(loadingVitalsTimerRef.current); loadingVitalsTimerRef.current = null; }
            setIsLoadingVitals(false);
          });
        getPatientForecasts(runId)
          .then((fcResp) => {
            const rows = Array.isArray(fcResp?.data) ? fcResp.data : [];
            if (rows.length) setRunForecasts(parseForecastRows(rows));
            else setRunForecasts(null);
          })
          .catch(() => setRunForecasts(null));
        
        // Fetch copilot response from database for all languages (polls while
        // the backend generates a not-yet-cached response).
        pollCopilotResponse(runId, language)
          .then(({ superseded, record }) => {
            if (superseded) return; // a newer fetch owns the loading state now
            setCopilotResponseText(record?.text || null);
            if (record?.run_id && record?.created_at) {
              setCopilotResponseMeta({
                run_id: record.run_id,
                created_at: record.created_at,
                tft_model: preferred?.tft_model ?? null,
                copilot_model: preferred?.copilot_model ?? null,
              });
            } else {
              setCopilotResponseMeta(null);
            }
            setIsTranslating(false);
          });
      })
      .catch(() => {
        setCopilotResponseText(null);
        setCopilotResponseMeta(null);
        setIsTranslating(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, language]);

  // Update copilot response when language changes
  const languageRef = React.useRef(language);
  React.useEffect(() => {
    // Skip on initial mount or if no run is selected
    if (languageRef.current === language || !modelRunId) {
      languageRef.current = language;
      return;
    }
    languageRef.current = language;

    // Show loading state for UX
    setIsTranslating(true);

    // Fetch copilot response from database for all languages (polls while
    // the backend generates a not-yet-cached response in the new language).
    pollCopilotResponse(modelRunId, language)
      .then(({ superseded, record }) => {
        if (superseded) return; // a newer fetch owns the loading state now
        setCopilotResponseText(record?.text || null);
        if (record?.run_id && record?.created_at) {
          const run = modelRunsRef.current.find((r) => r.model_run_id === modelRunId);
          setCopilotResponseMeta({
            run_id: record.run_id,
            created_at: record.created_at,
            tft_model: run?.tft_model ?? null,
            copilot_model: run?.copilot_model ?? null,
          });
        } else {
          setCopilotResponseMeta(null);
        }
        setIsTranslating(false);
      });
  }, [language, modelRunId, pollCopilotResponse]);

  // Poll for model run updates every minute to keep date chips current
  React.useEffect(() => {
    if (!patientId) return;

    const intervalId = setInterval(() => {
      getModelRunsByPatientId(patientId, 50, 0)
        .then((res) => {
          const runs = Array.isArray(res.data) ? res.data : [];
          setModelRuns(runs);
          setTotalRunCount(res.totalCount);
        })
        .catch((err) => {
          console.error('Failed to poll model runs:', err);
        });
    }, 180000); // Poll every 3 minutes (reduced from 1 minute to decrease load)

    return () => clearInterval(intervalId);
  }, [patientId]);

  React.useEffect(() => {
    // Handle /p/:patientId route - direct patient access
    if (patientIdParam) {
      const tempId = uuid();
      setConversationId(tempId);
      setPendingConversationId(tempId);
      // A fresh conversation is owned by the current user — no shared badge.
      setConversationOwner(null);
      // Only update patientId if it has changed - prevents unnecessary re-fetching
      if (patientIdRef.current !== patientIdParam) {
        setPatientId(patientIdParam);
      }
      return;
    }

    // Handle /c/:shortcode route - conversation access
    if (!shortcode) return;
    // Do NOT pass modelRunId here — that value is stale in this closure and belongs to
    // the previously-selected run, which causes a 404 when clicking a conversation from
    // a different run. Short codes are generated from UUIDs and are practically unique.
    getConversationByShortCode(shortcode)
      .then((res) => {
        setConversationId(res.data.conversation_id);
        setConversationOwner(
          res.data.user_id
            ? {
                id: String(res.data.user_id),
                name: res.data.owner_name ?? null,
                email: res.data.owner_email ?? null,
                avatarUrl: res.data.owner_avatar_url ?? null,
              }
            : null
        );
        // Only update patientId if it has changed - prevents unnecessary re-fetching
        // of metadata and thresholds when navigating between conversations of the same patient
        if (patientIdRef.current !== res.data.patient_id) {
          setPatientId(res.data.patient_id);
        }
        if (res.data.model_run_id) {
          conversationRunIdRef.current = res.data.model_run_id;
          // If the patient hasn't changed, the patientId effect won't re-fire.
          // Directly switch run data so vitals/forecasts/copilot update immediately.
          // BUT: Only switch if the run has actually changed to prevent unnecessary reloads
          if (patientIdRef.current === res.data.patient_id && modelRunId !== res.data.model_run_id) {
            switchToRun(res.data.model_run_id);
          }
        }
        setPendingConversationId(null);
      })
      .catch(() => {
        const tempId = uuid();
        setConversationId(tempId);
        setPendingConversationId(tempId);
        setConversationOwner(null);
        setPatientId(shortcode);
      });
  }, [shortcode, patientIdParam, switchToRun]);

  const mapConvToSidebar = (conv: any) => ({
    uuid: conv.conversation_id,
    text: conv.title,
    lastUpdated: conv.updated_at,
    shortCode: conv.short_code,
    status: conv.status,
    modelRunId: conv.model_run_id,
  });

  const sidebarReqToken = React.useRef(0);
  React.useEffect(() => {
    if (!user || !patientId) return;
    const token = ++sidebarReqToken.current;
    // The sidebar for a patient lists the caller's own conversations PLUS any
    // conversations shared with them for that same patient (flagged so the row
    // can show a share icon). A request token guards against stale overwrites.
    Promise.all([
      getUserConversations(String(user.user_id), patientId)
        .then((r) => r.data)
        .catch(() => []),
      getSharedWithMe()
        .then((r) => r.data)
        .catch(() => []),
    ]).then(([own, shared]: [any[], any[]]) => {
      if (sidebarReqToken.current !== token) return;
      setSharedConvoIds(new Set((shared || []).map((c: any) => c.conversation_id)));
      const ownRows = (own || []).map((c: any) => ({ ...mapConvToSidebar(c), shared: false }));
      const ownIds = new Set(ownRows.map((r) => r.uuid));
      const sharedRows = (shared || [])
        .filter(
          (c: any) => String(c.patient_id) === String(patientId) && !ownIds.has(c.conversation_id)
        )
        .map((c: any) => ({ ...mapConvToSidebar(c), shared: true }));
      const merged = [...sharedRows, ...ownRows].sort(
        (a, b) => new Date(b.lastUpdated).getTime() - new Date(a.lastUpdated).getTime()
      );
      setConversations(merged);
    });
  }, [patientId, user])

  React.useEffect(() => {
    if (!patientId) return;
    getPatientMetadata(patientId)
      .then((res) => {
        const data = res.data || {};
        const subjectId = String(data?.subject_id || patientId);

        // Build profile from OpenEMR normalized fields when available
        const fullName = [data?.title, data?.fname, data?.lname]
          .filter(Boolean)
          .join(" ")
          .trim();
        const addressParts = [data?.address, data?.city, data?.state]
          .filter(Boolean)
          .join(", ");

        const fallbackProfile: any = {
          name: `Patient ${subjectId}`,
          color: ramps.neutral[300],
          image: "",
          address: "",
          phone: "",
          email: "",
          gender: undefined,
          age: undefined,
        };

        const profile = {
          name: fullName || fallbackProfile.name,
          color: fallbackProfile.color,
          image: fallbackProfile.image,
          address: addressParts || fallbackProfile.address,
          phone: data?.phone || fallbackProfile.phone,
          email: data?.email || fallbackProfile.email,
        };
        const demographics = {
          gender: data?.gender || fallbackProfile.gender,
          age: data?.age ?? fallbackProfile.age,
          weight_kg: data?.weight_kg,
          height_m: data?.height_m,
        };

        setMeta({
          ...data,
          patient_id: data?.subject_id || patientId,
          profile,
          demographics,
        });
      }
      );
  }, [patientId]);

  React.useEffect(() => {
    if (!selectedDate && dateOptions.length) {
      setSelectedDate(dateOptions[dateOptions.length - 1].runId);
    }
  }, [dateOptions, selectedDate]);

  const handleSelectConversation = useCallback((id: string, shortCode: string) => {
    setPendingConversationId(null);
    setIsHistoryOpen(true);
    // Conversation URL has no ?run= — the run is derived from the conversation record
    navigate(`${ROUTES.CONVERSATION}/${shortCode}`);
  }, [navigate]);

  const handleNewConversation = useCallback(() => {
    const tempId = uuid();
    setConversationId(tempId);
    setPendingConversationId(tempId);
    setIsHistoryOpen(true);
    if (patientId) {
      // A new chat always opens on the latest vitals run, regardless of which
      // run is currently selected. `dateOptions` holds the 5 newest runs in
      // oldest-first order, so its last entry is the latest reading; fall back
      // to the first page of model runs if dateOptions isn't populated yet.
      const latestRunId =
        dateOptions.length > 0
          ? dateOptions[dateOptions.length - 1].runId
          : modelRuns[0]?.model_run_id ?? null;
      const runSuffix = latestRunId ? `?run=${latestRunId}` : "";
      navigate(`${ROUTES.PATIENT_BY_ID}/${patientId}${runSuffix}`);
    }
  }, [patientId, dateOptions, modelRuns, navigate]);

  const handleCreateConversation = useCallback(async () => {
    if (!patientId) {
      throw new Error("patientId is missing");
    }
    if (!user) {
      throw new Error("user is missing");
    }
    const shortCode = uuid().replace(/-/g, "").slice(0, 10);
    if (!modelRunId) {
      throw new Error("modelRunId is missing");
    }
    const res = await createUserConversation({
      title: "New Chat",
      user_id: String(user.user_id),
      patient_id: patientId,
      model_run_id: modelRunId,
      short_code: shortCode,
    });
    setConversations((prev) => [
      {
        uuid: res.data.conversation_id,
        text: res.data.title,
        lastUpdated: res.data.updated_at,
        shortCode: res.data.short_code,
        status: res.data.status ?? "active",
        modelRunId: modelRunId ?? undefined,
      },
      ...prev,
    ]);
    setConversationId(res.data.conversation_id);
    setPendingConversationId(null);
    navigate(`${ROUTES.CONVERSATION}/${res.data.short_code}`);

    return res.data;
  }, [modelRunId, patientId, user, navigate]);

  const handleConversationSummary = useCallback((id: string, summary: string) => {
    setConversations((prev) =>
      prev.map((conv) =>
        conv.uuid === id ? { ...conv, text: summary, lastUpdated: new Date().toISOString() } : conv
      )
    );
  }, []);

  const handleUpdateConversationSummary = useCallback(async (id: string, summary: string) => {
    await updateConversationSummary(id, summary);
    setConversations((prev) =>
      prev.map((conv) =>
        conv.uuid === id ? { ...conv, text: summary, lastUpdated: new Date().toISOString() } : conv
      )
    );
  }, []);

  const handleArchiveConversation = useCallback(async (id: string, nextStatus: "active" | "archived") => {
    await updateConversationStatus(id, nextStatus);
    setConversations((prev) =>
      prev.map((conv) =>
        conv.uuid === id ? { ...conv, status: nextStatus } : conv
      )
    );
    if (conversationId === id) {
      setConversationId(null);
    }
  }, [conversationId]);

  const handleDeleteConversation = useCallback(async (id: string) => {
    await deleteConversation(id);
    setConversations((prev) =>
      prev.filter((conv) => conv.uuid !== id)
    );
    if (conversationId === id) {
      setConversationId(null);
    }
  }, [conversationId]);

  const handleCloseHistory = useCallback(() => setIsHistoryOpen(false), []);
  const handleOpenHistory = useCallback(() => setIsHistoryOpen(true), []);

  const handleLogoClick = useCallback(() => {
    // Reset all state to ensure clean navigation
    setConversationId(null);
    setPendingConversationId(null);
    setPatientId(null);
    setModelRunId(null);
    setIsHistoryOpen(false);
    // Force navigation to dashboard
    navigate("/", { replace: true });
  }, [navigate]);

  const handleSelectRun = useCallback((runId: string) => {
    // Only switch run if it's actually different to avoid unnecessary reloads
    if (modelRunId !== runId) {
      switchToRun(runId);
    }
    // Chip / AllRuns click always goes to /p/:patientId?run=<runId> with a fresh pending conversation
    const tempId = uuid();
    setConversationId(tempId);
    setPendingConversationId(tempId);
    if (patientId) navigate(`${ROUTES.PATIENT_BY_ID}/${patientId}?run=${runId}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, navigate, switchToRun, modelRunId]);

  // If both shortcode and patientIdParam are undefined (navigated away), don't render Patient content
  if (!shortcode && !patientIdParam) {
    return null;
  }

  // The open conversation is "not owned by me" when it appears in my shared list
  // OR its loaded owner record differs from me. Anyone who is not the owner gets
  // a read-only chat with owner-only controls (share, threshold edit, memory)
  // hidden — regardless of role.
  const isSharedConversation = !!conversationId && sharedConvoIds.has(conversationId);
  const isNotOwner =
    isSharedConversation ||
    (!!conversationOwner && !!user && conversationOwner.id !== String(user.user_id));

  return (
    <>
      <Sidebar
        open={isHistoryOpen}
        conversations={conversations}
        selectedId={conversationId}
        streamingIds={streamingIds}
        modelRuns={modelRuns}
        formatRunLabel={formatRunLabel}
        onSelect={handleSelectConversation}
        onNewConversation={handleNewConversation}
        onArchiveConversation={handleArchiveConversation}
        onDeleteConversation={handleDeleteConversation}
        onConversationSummary={handleConversationSummary}
        onUpdateConversationSummary={handleUpdateConversationSummary}
        onClose={handleCloseHistory}
        onOpen={handleOpenHistory}
        onLogoClick={handleLogoClick}
        readOnly={isNotOwner}
      />
      <Grid
        container
        padding={0}
        spacing={0}
        columns={20}
        sx={{ overflow: "hidden", height: "100vh", minHeight: 0 }}
      >
        <Grid
          size={{ xs: isHistoryOpen ? 4 : 0 }}
          sx={{ transition: "max-width 0.3s ease, flex-basis 0.3s ease" }}
        />
        <S.MainGrid size={{ xs: isHistoryOpen ? 11 : 15 }} isHistoryOpen={isHistoryOpen}>
          <S.ChatBoxWrapper>
            <AllRuns
              initialRuns={modelRuns}
              totalRunCount={totalRunCount}
              patientId={patientId ?? ""}
              selectedRunId={selectedDate}
              dateOptions={dateOptions}
              onSelectRun={handleSelectRun}
              language={language}
              formatRunLabel={formatRunLabel}
              selectedModel={selectedModel}
              onSelectModel={setSelectedModel}
              conversationTitle={
                conversationId
                  ? (() => {
                      const title = conversations.find((c) => c.uuid === conversationId)?.text?.trim();
                      // Hide placeholder titles for fresh, untitled conversations.
                      if (!title) return null;
                      const lower = title.toLowerCase();
                      if (lower === "new chat" || title === t('sidebar.newChat')) return null;
                      return title;
                    })()
                  : null
              }
            />
            <Chat
              meta={meta}
              patientId={patientId ?? undefined}
              modelRunId={modelRunId}
              copilotModel={selectedModel}
              conversationId={conversationId}
              pendingConversationId={pendingConversationId}
              initialCopilotResponse={copilotResponseText}
              isTranslatingCopilot={isTranslating}
              copilotResponseMeta={copilotResponseMeta}
              onCreateConversation={handleCreateConversation}
              onConversationsChange={(list) => setConversations(list)}
              onConversationChange={(id) => setConversationId(id)}
              onConversationSummary={handleConversationSummary}
              readOnly={isNotOwner}
              owner={
                isNotOwner && conversationOwner
                  ? {
                      name: conversationOwner.name,
                      email: conversationOwner.email,
                      avatarUrl: conversationOwner.avatarUrl,
                    }
                  : null
              }
            />
          </S.ChatBoxWrapper>
        </S.MainGrid>
        <Grid size={{ xs: 5 }} sx={{ p: 1, height: "100%", display: "flex", flexDirection: "column", overflow: "hidden" }}>
          <Box sx={{ flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
            <Tabs
              value={sideTab}
              onChange={(_, v) => setSideTab(v)}
              variant="standard"
              sx={{
                minHeight: 24,
                "& .MuiTabs-indicator": { display: "none" },
                "& .MuiTab-root": {
                  minHeight: 24,
                  height: 24,
                  py: 0,
                  px: 1,
                  fontFamily: fonts.brand,
                  fontSize: 11,
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                  borderRadius: "4px",
                  color: "text.secondary",
                  transition: "background-color 0.15s, color 0.15s",
                  "&:hover": { bgcolor: "action.hover" },
                  "&.Mui-selected": {
                    color: rolesFor(isDark).onPrimary,
                    bgcolor: rolesFor(isDark).primary,
                  },
                },
              }}
            >
              <Tab label={t('tab.vitals')} />
              {!isNotOwner && <Tab label={t('tab.memory')} />}
            </Tabs>
            {conversationId && conversationId !== pendingConversationId && !isNotOwner ? (
              <ShareConversationButton
                conversationId={conversationId}
                conversationTitle={
                  conversations.find((c) => c.uuid === conversationId)?.text
                }
              />
            ) : null}
          </Box>

          <Box sx={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column", minHeight: 0 }}>
            {sideTab === 0 && (
              <Box sx={{ overflowY: "auto", flex: 1, "&::-webkit-scrollbar": { display: "none" } }}>
                <PatientProfile meta={meta} thresholds={thresholds} patientId={patientId} isLoadingThresholds={isLoadingThresholds} readOnly={isNotOwner} />
                {isLoadingVitals ? (
                  <Box sx={{ px: 1, pt: 1 }}>
                    {Array.from({ length: 4 }).map((_, idx) => (
                      <Box key={idx} sx={{ mb: 2, display: "flex", alignItems: "flex-start" }}>
                        <Box sx={{ width: 120, flexShrink: 0 }}>
                          <Skeleton variant="text" width="80%" height={14} />
                          <Skeleton variant="text" width={56} height={32} />
                          <Skeleton variant="text" width="60%" height={14} />
                        </Box>
                        <Skeleton variant="rectangular" sx={{ flex: 1, borderRadius: 1 }} height={110} />
                      </Box>
                    ))}
                  </Box>
                ) : (runVitals || meta?.vitals) ? (() => {
                  const v = runVitals ?? meta?.vitals;
                  const lastNonNull = (arr: (number | null)[]): number | null => {
                    for (let i = arr.length - 1; i >= 0; i--) {
                      if (arr[i] != null) return arr[i] as number;
                    }
                    return null;
                  };
                  const systolicBP = lastNonNull(v.systolic_blood_pressure);

                  const readings = [
                    { label: t('vitals.systolicBP'), value: systolicBP, unit: "mmHg", color: vitalsColors.SBP, fcProp: "systolic_blood_pressure" as const, vitalType: "systolic_blood_pressure", vitalKey: "SBP" as const },
                    { label: t('vitals.diastolicBP'), value: lastNonNull(v.diastolic_blood_pressure), unit: "mmHg", color: vitalsColors.DBP, fcProp: "diastolic_blood_pressure" as const, vitalType: "diastolic_blood_pressure", vitalKey: "DBP" as const },
                    { label: t('vitals.heartRate'), value: lastNonNull(v.heart_rate), unit: "bpm", color: vitalsColors.HR, fcProp: "heart_rate" as const, vitalType: "heart_rate", vitalKey: "HR" as const },
                    { label: t('vitals.spo2'), value: lastNonNull(v.o2_saturation), unit: "%", color: vitalsColors.SpO2, fcProp: "o2_saturation" as const, vitalType: "o2_saturation", vitalKey: "SpO2" as const },
                  ].filter((r) => r.value != null);

                  const chartData = buildVitalsData({
                    charttime: v.charttime || [],
                    systolic_blood_pressure: v.systolic_blood_pressure || [],
                    diastolic_blood_pressure: v.diastolic_blood_pressure || [],
                    heart_rate: v.heart_rate || [],
                    o2_saturation: v.o2_saturation || [],
                    forecasts: runForecasts ?? undefined,
                  });
                  return (
                    <Box sx={{ px: 1, pt: 1 }}>
                      {readings.map((r) => {
                        const nextFc = runForecasts?.[r.fcProp]?.find((p) => p.time_idx === 1);
                        const vitalThresholds = thresholds?.thresholds?.filter(
                          (t: any) => t.vital_type === r.vitalType
                        ) || [];
                        const hasChart = chartData && chartData.hasData[r.vitalKey];
                        return (
                          <Box key={r.label} sx={{ mb: 2, display: "flex", alignItems: "flex-start", gap: 0.5 }}>
                            <Box sx={{ width: hasChart ? 120 : "100%", flexShrink: 0 }}>
                              <LastReadingCard
                                label={r.label}
                                value={r.value as number}
                                unit={r.unit}
                                color={r.color}
                                nextForecastValue={nextFc?.value ?? null}
                                vitalType={r.vitalType}
                                thresholds={vitalThresholds}
                              />
                            </Box>
                            {hasChart && (
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <SingleVitalChart
                                  vitalKey={r.vitalKey}
                                  data={chartData.data}
                                  hasForecast={chartData.hasForecast}
                                  tickInterval={chartData.tickInterval}
                                  height={110}
                                />
                              </Box>
                            )}
                          </Box>
                        );
                      })}
                    </Box>
                  );
                })() : (
                  <Box sx={{ px: 2, pt: 2, fontSize: 12, color: "text.secondary" }}>{t('vitals.noData')}</Box>
                )}
              </Box>
            )}
            {/* Memory tab: Graphiti facts. Patient profile now lives at the top of the Vitals tab. */}
            {sideTab === 1 && !isNotOwner && (
              <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
                <FactsPanel patientId={patientId} />
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>

    </>
  );
}
